import { RUN_START_LINES } from '../config/dialogue';
import { MAX_FRAME_TIME, WAKE_UP } from '../config/gameplay';
import { clamp, smoothStep } from '../utils/math';

/** Presentation clock only: no simulation steps or replay inputs during waking. */
export class WakeUpSequence {
  private age = 0;
  private riseAge = 0;
  private phase: 'waiting' | 'shout' | 'rise' = 'waiting';

  get progress(): number {
    return this.phase === 'rise' ? clamp(this.riseAge / WAKE_UP.duration, 0, 1) : 0;
  }

  get done(): boolean { return this.progress === 1; }
  get shouting(): boolean { return this.phase === 'shout'; }

  /** HUD pre-roll only; zero is reached exactly when controls unlock. */
  get countdownMs(): number {
    if (this.done) return 0;
    const line = RUN_START_LINES[0];
    // Speech can outlast its subtitle. Reserve the full rise until it ends.
    const beforeRise = this.phase === 'rise' ? 0
      : Math.max(0, line.startTime + line.duration - this.age);
    return -Math.ceil((beforeRise + WAKE_UP.duration - this.riseAge) * 1000);
  }

  get visual(): { opening: number; blur: number } {
    const p = this.progress;
    // Let the room show through as soon as the call begins. Keep the eyes
    // partly open until the rise finishes, without closing them again.
    const shoutProgress = clamp(
      (this.age - RUN_START_LINES[0].startTime) / WAKE_UP.shoutEyeDuration, 0, 1,
    );
    const opening = this.phase === 'waiting' ? 0
      : this.phase === 'shout' ? WAKE_UP.shoutEyeOpening * smoothStep(shoutProgress)
      : WAKE_UP.shoutEyeOpening + (1 - WAKE_UP.shoutEyeOpening) * smoothStep(p);
    return {
      opening,
      blur: WAKE_UP.blurPixels * (1 - smoothStep(p)) * (0.8 + 0.2 * Math.cos(p * Math.PI * 6)),
    };
  }

  advance(seconds: number, speaking: boolean): 'shout' | 'rise' | null {
    const dt = clamp(seconds, 0, MAX_FRAME_TIME);
    this.age += dt;
    const line = RUN_START_LINES[0];
    if (this.phase === 'waiting' && this.age >= line.startTime) {
      this.phase = 'shout';
      return 'shout';
    }
    // The subtitle duration also covers muted audio or a missing TTS engine.
    if (this.phase === 'shout' && this.age >= line.startTime + line.duration && !speaking) {
      this.phase = 'rise';
      return 'rise';
    }
    if (this.phase === 'rise') {
      this.riseAge = Math.min(WAKE_UP.duration, this.riseAge + dt);
      // Repeated frame additions can leave an infinitesimal remainder at zero.
      if (WAKE_UP.duration - this.riseAge < 1e-9) this.riseAge = WAKE_UP.duration;
    }
    return null;
  }
}
